// @ts-nocheck
import React, { useState, useMemo, useEffect, useCallback } from "react";
import { useNavigate, useRouterState } from "@tanstack/react-router";
import ToolingTrackingBoard, {
  MOLD_PLANNING_EXPORT_COLUMNS,
  toMoldPlanningExportRow,
} from "./ToolingTrackingBoard";
import {
  MoreHorizontal,
  Pencil,
  Trash2,
  ArrowUpRight,
  MapPin,
  Wrench,
  Plus,
  Download,
  AlertCircle,
  Scissors,
  Image as ImageIcon,
  CheckCircle2,
  Clock,
  Activity,
  AlertTriangle,
  Tag,
  Filter,
  X,
  Hammer,
  Layers,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn, formatLocation, formatNumber } from "@/lib/utils";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { trpcClient } from "@/lib/trpc";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { getStatusConfig } from "@/config/status-registry";
import { useExcelExport } from "@/hooks/useExcelExport";
import { exportToExcel, type ExcelColumnDef } from "@/lib/excel.utils";
import { getFullUrl } from "@/lib/uploadFile";
import { useRowSelection } from "@/hooks/useRowSelection";
import { DataTableRow } from "@/components/shared/DataTableRow";
import {
  ListPageShell,
  useServerListFilter,
} from "@/components/shared/ListPageShell";
import {
  InlineFilterRow,
  type FilterField,
  CHECKBOX_COL_NULL,
} from "@/components/shared/InlineFilterRow";
import type { ActiveFilters } from "@/components/shared/FilterBar";
import {
  CompactKpiRibbon,
  type CompactKpiItem,
} from "@/components/shared/CompactKpiRibbon";
import {
  DatePeriodFilter,
  useDatePeriodFilter,
} from "@/components/shared/DatePeriodFilter";
import { SortableHeader } from "@/components/shared/SortableHeader";
import {
  BulkActionBar,
  type BulkAction,
} from "@/components/shared/BulkActionBar";
import QuickRelocateModal from "./QuickRelocateModal";
import PrintDialog from "@/components/shared/PrintDialog";
import MoldLabelPrintTemplate from "./print/MoldLabelPrintTemplate";
import { MoldWearBar } from "./MoldWearBar";
import {
  getMoldTypeVisualMeta,
  isMoldTypeExcluded,
  normalizeMoldTypeCode,
  useMoldTypeTaxonomy,
} from "./mold-taxonomy";
import { emsTabClass } from "@/components/ui/ems-tabs";
import { usePageSizeState } from "@/hooks/usePreferredPageSize";

const CATALOG_EXPORT_COLUMNS: ExcelColumnDef[] = [
  { key: "moldId", label: "Mã khuôn", width: 18 },
  { key: "name", label: "Tên khuôn", width: 28 },
  // Sản phẩm và khách hàng tách 2 cột (trước gộp 1 cột "Sản phẩm / Khách hàng"), 05/11/2026
  { key: "productName", label: "Sản phẩm", width: 28 },
  { key: "customerName", label: "Khách hàng", width: 24 },
  { key: "typeLabel", label: "Phân loại khuôn", width: 22 },
  { key: "location", label: "Vị trí kệ", width: 20 },
  { key: "usageCount", label: "Lượt dập lũy kế", width: 16 },
  { key: "statusLabel", label: "Trạng thái", width: 16 },
];

type MoldPageView = "catalog" | "planning";

function resolveMoldPageView(rawView?: unknown): MoldPageView {
  if (rawView === "planning" || rawView === "tracking") return "planning";
  return "catalog";
}

function resolveInitialMoldType(rawType?: unknown): string {
  if (!rawType) return "ALL";
  const str = String(rawType).trim();
  if (!str || str.toUpperCase() === "ALL" || isMoldTypeExcluded(str)) {
    return "ALL";
  }
  return normalizeMoldTypeCode(str);
}

function countNonEmptyFilters(values: ActiveFilters): number {
  return Object.values(values || {}).filter((v) => {
    if (!v) return false;
    if (typeof v === "string") return Boolean(v.trim());
    if (Array.isArray(v)) return v.length > 0;
    if (typeof v === "object") return Boolean((v as any).from || (v as any).to);
    return false;
  }).length;
}

export default function MoldsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const routerSearch = useRouterState({
    select: (s) => s.location.search,
  }) as Record<string, any>;

  const rawView = routerSearch?.view;
  const rawType = routerSearch?.type;
  const urlQ = String(routerSearch?.q || routerSearch?.search || "").trim();

  const [currentView, setCurrentView] = useState<MoldPageView>(() =>
    resolveMoldPageView(rawView),
  );
  const [selectedType, setSelectedType] = useState<string>(() =>
    resolveInitialMoldType(rawType),
  );

  // State cho tab "Kế hoạch khuôn"
  const [planningPage, setPlanningPage] = useState<number>(1);
  const [planningPageSize, setPlanningPageSize] = usePageSizeState(20);
  const [planningStatus, setPlanningStatus] = useState<string>("ALL");
  const [planningShowFilter, setPlanningShowFilter] = useState<boolean>(false);
  const [planningFilterValues, setPlanningFilterValues] = useState<ActiveFilters>({});
  const [isExportingPlanning, setIsExportingPlanning] = useState<boolean>(false);
  const planningDate = useDatePeriodFilter("all");

  // Modals cho tab "Danh mục khuôn"
  const [moldToDelete, setMoldToDelete] = useState<{ id: string; name: string } | null>(null);
  const [relocateMold, setRelocateMold] = useState<any | null>(null);
  const [printLabelMold, setPrintLabelMold] = useState<any | null>(null);

  const { taxonomyItems, taxonomyByCode } = useMoldTypeTaxonomy();

  useEffect(() => {
    setCurrentView(resolveMoldPageView(rawView));
  }, [rawView]);

  useEffect(() => {
    setSelectedType(resolveInitialMoldType(rawType));
  }, [rawType]);

  // ── Truy vấn Danh mục khuôn ─────────────────────────────────────────────
  const { data: catalogStats } = useQuery({
    queryKey: ["molds-stats", selectedType],
    queryFn: () =>
      trpcClient.technical.molds.stats.query({
        type: selectedType === "ALL" ? undefined : selectedType,
      }),
  });

  const catalogFilterColumns = useMemo(
    () => [
      CHECKBOX_COL_NULL,
      { key: "moldId", label: "Lọc mã khuôn...", type: "text" as const },
      { key: "name", label: "Lọc tên / SP...", type: "text" as const },
      {
        key: "type",
        label: "Phân loại",
        type: "select" as const,
        options: taxonomyItems.map((t) => ({
          value: t.canonicalCode,
          label: t.pillLabel,
        })),
      },
      { key: "location", label: "Lọc vị trí kệ...", type: "text" as const },
      null,
      {
        key: "status",
        label: "Trạng thái",
        type: "select" as const,
        options: [
          { value: "AVAILABLE", label: "Sẵn sàng" },
          { value: "IN_USE", label: "Đang gá máy" },
          { value: "PENDING", label: "Chờ xử lý" },
          { value: "MAINTENANCE", label: "Bảo dưỡng" },
          { value: "DAMAGED", label: "Hỏng / Sự cố" },
          { value: "DISCARDED", label: "Thanh lý" },
        ],
      },
      null,
    ],
    [taxonomyItems],
  );

  const catalogFilterFields = useMemo(
    () =>
      catalogFilterColumns.filter((f): f is FilterField =>
        Boolean(f && (f as any).key),
      ),
    [catalogFilterColumns],
  );

  const {
    items,
    isLoading: isCatalogLoading,
    filterValues,
    showFilter,
    currentPage,
    pageSize,
    totalItems,
    statusFilter,
    onStatusChange,
    onFilterChange,
    onPageChange,
    onPageSizeChange,
    onToggleFilter,
    onClearAll,
    sortField,
    sortOrder,
    handleSort,
  } = useServerListFilter<any>({
    queryKey: ["molds", selectedType],
    queryFn: (params) =>
      trpcClient.technical.molds.list.query({
        ...params,
        type: selectedType === "ALL" ? undefined : selectedType,
      }) as any,
    defaultLimit: 50,
  });

  const displayItems = useMemo(() => {
    return items.filter((item: any) => {
      if (isMoldTypeExcluded(item.type)) return false;
      const code = (item.moldId || item.dieCutId || item.id || "").toLowerCase();
      if (filterValues.moldId && !code.includes(String(filterValues.moldId).toLowerCase())) {
        return false;
      }
      const name = `${item.name || ""} ${item.productName || ""} ${item.customer?.fullName || item.customer?.name || ""}`.toLowerCase();
      if (filterValues.name && !name.includes(String(filterValues.name).toLowerCase())) {
        return false;
      }
      if (
        filterValues.type &&
        normalizeMoldTypeCode(item.type) !== normalizeMoldTypeCode(String(filterValues.type))
      ) {
        return false;
      }
      const loc = (item.location || "").toLowerCase();
      if (filterValues.location && !loc.includes(String(filterValues.location).toLowerCase())) {
        return false;
      }
      if (filterValues.status && item.status !== filterValues.status) return false;
      return true;
    });
  }, [items, filterValues]);

  // ── Truy vấn Kế hoạch khuôn ─────────────────────────────────────────────
  const planningQueryInput = useMemo(
    () => ({
      page: planningPage,
      limit: planningPageSize,
      search: urlQ || undefined,
      status: planningStatus !== "ALL" ? planningStatus : undefined,
      toolingType: selectedType !== "ALL" ? selectedType : undefined,
      dateFrom: planningDate.dateParams.from,
      dateTo: planningDate.dateParams.to,
      orderCode: (planningFilterValues.orderCode as string) || undefined,
      moldCode: (planningFilterValues.moldCode as string) || undefined,
      processName: (planningFilterValues.processName as string) || undefined,
      location: (planningFilterValues.location as string) || undefined,
    }),
    [
      planningPage,
      planningPageSize,
      urlQ,
      planningStatus,
      selectedType,
      planningDate.dateParams.from,
      planningDate.dateParams.to,
      planningFilterValues,
    ],
  );

  const {
    data: planningData,
    isLoading: isPlanningLoading,
    refetch: refetchPlanning,
  } = useQuery({
    queryKey: ["tooling-tracking-board", planningQueryInput],
    queryFn: () => trpcClient.productionOrders.toolingBoard.query(planningQueryInput),
  });

  const planningItems = planningData?.items || [];
  const planningMeta = planningData?.meta || {
    total: 0,
    page: 1,
    limit: planningPageSize,
    totalPages: 1,
  };
  const planningStats = planningData?.stats || {
    total: 0,
    allTypesTotal: 0,
    newCount: 0,
    inProductionCount: 0,
    readyCount: 0,
    reuseCount: 0,
    pendingCount: 0,
    byType: {} as Record<string, number>,
  };

  // ── Mutations (Danh mục khuôn) ──────────────────────────────────────────
  const deleteMutation = useMutation({
    mutationFn: (id: string) => trpcClient.technical.molds.delete.mutate({ id }),
    onSuccess: () => {
      toast.success("Đã xóa khuôn thành công");
      queryClient.invalidateQueries({ queryKey: ["molds"] });
      queryClient.invalidateQueries({ queryKey: ["molds-stats"] });
      queryClient.invalidateQueries({ queryKey: ["tooling-tracking-board"] });
      setMoldToDelete(null);
    },
    onError: (err: any) => {
      toast.error(err.message || "Không thể xóa khuôn đã có lịch sử sản xuất");
    },
  });

  const updateStatusMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      trpcClient.technical.molds.updateStatus.mutate({ id, status }),
    onSuccess: () => {
      toast.success("Đã cập nhật trạng thái khuôn");
      queryClient.invalidateQueries({ queryKey: ["molds"] });
      queryClient.invalidateQueries({ queryKey: ["molds-stats"] });
    },
    onError: (err: any) => {
      toast.error(err.message || "Lỗi cập nhật trạng thái khuôn");
    },
  });

  // ── Chọn dòng & Xuất Excel ──────────────────────────────────────────────
  const rowIds = displayItems.map((m: any) => m.moldId || m.dieCutId || m.id);
  const rowSelection = useRowSelection(rowIds);
  const {
    selectedIds,
    selectionMode,
    toggleSelect,
    toggleSelectAll,
    allSelected,
    selectedCount,
    clearSelection,
  } = rowSelection;

  const formatCatalogExportRow = useCallback(
    (m: any) => {
      const canonical = normalizeMoldTypeCode(m.type);
      const visual =
        taxonomyByCode.get(canonical) || getMoldTypeVisualMeta(m.type);
      const customerDisplay = m.customer?.fullName || m.customer?.name || "";
      return {
        moldId: m.moldId || m.dieCutId || m.id || "",
        name: m.name || "",
        productName: m.productName || m.product?.productName || "",
        customerName: customerDisplay,
        typeLabel: `${visual.prefix} • ${visual.shortLabel}`,
        location: formatLocation(m.location) || "",
        usageCount: Number(m.usageCount || 0),
        statusLabel: getStatusConfig("ToolingStatus", m.status).label || m.status || "",
      };
    },
    [taxonomyByCode],
  );

  const { handleExport: handleCatalogExport, isExporting: isExportingCatalog } =
    useExcelExport({
      filename: "danh_muc_khuon",
      columns: CATALOG_EXPORT_COLUMNS,
      serverQuery: async (filters) => {
        const res = await trpcClient.technical.molds.list.query({
          ...filters,
          type: selectedType === "ALL" ? undefined : selectedType,
          status:
            !statusFilter || statusFilter === "ALL" ? undefined : statusFilter,
          page: 1,
          limit: 5000,
        });
        const rawList = (res as any)?.items || (res as any) || [];
        return rawList.map(formatCatalogExportRow);
      },
      filterValues,
      localData: displayItems.map(formatCatalogExportRow),
      selectedIds: Array.from(selectedIds),
      idKey: "moldId",
    });

  const handlePlanningExport = useCallback(async () => {
    try {
      setIsExportingPlanning(true);
      const res = await trpcClient.productionOrders.toolingBoard.query({
        ...planningQueryInput,
        page: 1,
        limit: 5000,
      });
      const rows = (res?.items || []).map((item: any) =>
        toMoldPlanningExportRow(item, taxonomyByCode),
      );
      if (rows.length === 0) {
        toast.warning("Không có dữ liệu kế hoạch khuôn phù hợp bộ lọc để xuất Excel");
        return;
      }
      await exportToExcel(rows, MOLD_PLANNING_EXPORT_COLUMNS, "ke_hoach_khuon");
    } catch (err: any) {
      toast.error(err?.message || "Lỗi khi xuất Excel kế hoạch khuôn");
    } finally {
      setIsExportingPlanning(false);
    }
  }, [planningQueryInput, taxonomyByCode]);

  const handleUnifiedExport = useCallback(() => {
    if (currentView === "planning") {
      void handlePlanningExport();
      return;
    }
    handleCatalogExport();
  }, [currentView, handlePlanningExport, handleCatalogExport]);

  const getCatalogBulkActions = (): BulkAction[] => {
    if (!selectedCount) return [];
    return [
      {
        label: "Xuất danh sách",
        icon: <Download className="w-3.5 h-3.5" />,
        onClick: () => {
          handleCatalogExport();
          clearSelection();
        },
      },
    ];
  };

  // ── Điều hướng Tabs & Lọc Tags (Anti-Trap Rule 3.3) ─────────────────────
  const handleViewChange = (v: MoldPageView) => {
    setCurrentView(v);
    clearSelection();
    (navigate as any)({
      search: (prev: any) => ({
        ...prev,
        view: v === "planning" ? "planning" : undefined,
      }),
      replace: true,
    });
  };

  const handleSelectMoldType = (key: string | null) => {
    const nextType = !key || key === "ALL" || key === selectedType ? "ALL" : key;
    setSelectedType(nextType);
    onPageChange(1);
    setPlanningPage(1);
    clearSelection();
    (navigate as any)({
      search: (prev: any) => ({
        ...prev,
        type: nextType === "ALL" ? undefined : nextType,
      }),
      replace: true,
    });
  };

  const activeCatalogStatusKey =
    (Array.isArray(statusFilter) ? statusFilter[0] : (statusFilter as string)) || "ALL";

  const handleSelectCatalogStatus = (key: string | null) => {
    if (!key || key === "ALL") {
      onStatusChange("");
    } else {
      const current = Array.isArray(statusFilter) ? statusFilter[0] : statusFilter;
      onStatusChange(key === current ? "" : key);
    }
  };

  const handleSelectPlanningStatus = (key: string | null) => {
    setPlanningStatus((prev) => (!key || key === "ALL" || key === prev ? "ALL" : key));
    setPlanningPage(1);
  };

  // ── Số liệu cho Dòng 1, Dòng 2 (6 Phân loại khuôn) & Dòng 3 (Trạng thái) ─
  const catalogAllTypesTotal =
    catalogStats?.allTypesTotal ?? catalogStats?.total ?? totalItems;
  const planningAllTypesTotal =
    planningStats?.allTypesTotal ?? planningStats?.total ?? 0;

  const activeByType =
    currentView === "catalog"
      ? catalogStats?.byType || {}
      : planningStats?.byType || {};

  const moldTypeRibbonItems = useMemo<CompactKpiItem[]>(() => {
    const allCount =
      currentView === "catalog" ? catalogAllTypesTotal : planningAllTypesTotal;
    return [
      {
        key: "ALL",
        label: "Tất cả",
        count: allCount,
        variant: "default",
        tooltip: "Hiển thị tất cả 6 phân loại khuôn gia công",
        testId: "mold-type-tag-ALL",
      },
      ...taxonomyItems.map((t) => ({
        key: t.canonicalCode,
        label: t.pillLabel,
        count: activeByType[t.canonicalCode] ?? 0,
        variant: t.kpiVariant,
        tooltip: t.fullLabel,
        testId: `mold-type-tag-${t.canonicalCode}`,
      })),
    ];
  }, [
    currentView,
    catalogAllTypesTotal,
    planningAllTypesTotal,
    taxonomyItems,
    activeByType,
  ]);

  const catalogStatusRibbonItems = useMemo<CompactKpiItem[]>(
    () => [
      {
        key: "ALL",
        label: "Tất cả",
        count: catalogStats?.total ?? totalItems,
        icon: Scissors,
        variant: "default",
        tooltip: "Tổng khuôn theo phân loại đang chọn",
      },
      {
        key: "AVAILABLE",
        label: "Sẵn sàng",
        count: catalogStats?.available ?? 0,
        icon: CheckCircle2,
        variant: "emerald",
        tooltip: "Sẵn sàng tại kệ để cấp cho xưởng",
      },
      {
        key: "IN_USE",
        label: "Đang gá máy",
        count: catalogStats?.inUse ?? 0,
        icon: Activity,
        variant: "blue",
        tooltip: "Đang chạy trong ca sản xuất",
      },
      {
        key: "PENDING",
        label: "Chờ xử lý",
        count: catalogStats?.pending ?? 0,
        icon: Clock,
        variant: "amber",
        tooltip: "Chờ nghiệm thu hoặc nhập kệ",
      },
      {
        key: "MAINTENANCE",
        label: "Bảo dưỡng",
        count: catalogStats?.maintenance ?? 0,
        icon: Wrench,
        variant: "purple",
        tooltip: "Đang mài dao / sửa chữa định kỳ",
      },
      {
        key: "NEEDS_MAINTENANCE",
        label: "Cần bảo dưỡng",
        count: catalogStats?.needsMaintenance ?? 0,
        icon: Wrench,
        variant: "amber",
        animate: (catalogStats?.needsMaintenance ?? 0) > 0,
        tooltip: "Vượt chu kỳ bảo dưỡng dao định kỳ",
      },
      {
        key: "DAMAGED",
        label: "Hỏng",
        count: catalogStats?.damaged ?? 0,
        icon: AlertTriangle,
        variant: "rose",
        animate: (catalogStats?.damaged ?? 0) > 0,
        tooltip: "Cần kiểm tra khắc phục hoặc thay thế",
      },
      {
        key: "CRITICAL_WEAR",
        label: "Sắp hết hạn",
        count: catalogStats?.criticalWear ?? 0,
        icon: AlertTriangle,
        variant: "rose",
        animate: (catalogStats?.criticalWear ?? 0) > 0,
        tooltip: "Đạt ≥ 85% định mức tuổi thọ tối đa",
      },
      {
        key: "DISCARDED",
        label: "Thanh lý",
        count: catalogStats?.discarded ?? 0,
        variant: "slate",
        tooltip: "Khuôn đã hủy / thanh lý",
      },
    ],
    [catalogStats, totalItems],
  );

  const planningStatusRibbonItems = useMemo<CompactKpiItem[]>(
    () => [
      {
        key: "ALL",
        label: "Tất cả",
        count: planningStats.total,
        icon: Layers,
        variant: "default",
      },
      {
        key: "PENDING",
        label: "Chờ chuẩn bị",
        count: planningStats.pendingCount,
        icon: Clock,
        variant: "amber",
        tooltip: "Đang đợi kiểm tra kho hoặc đặt làm ngoài",
      },
      {
        key: "NEW",
        label: "Cần làm mới",
        count: planningStats.newCount,
        icon: AlertTriangle,
        variant: "rose",
        animate: planningStats.newCount > 0,
        tooltip: "Chưa có khuôn trong kho, cần gia công mới",
      },
      {
        key: "IN_PRODUCTION",
        label: "Đang làm ngoài",
        count: planningStats.inProductionCount,
        icon: Hammer,
        variant: "purple",
        tooltip: "Đã đặt nhà cung cấp làm khuôn, chờ giao nghiệm thu",
      },
      {
        key: "REUSE",
        label: "Dùng lại từ kho",
        count: planningStats.reuseCount,
        icon: Scissors,
        variant: "blue",
        tooltip: "Sử dụng lại khuôn có sẵn trong kho",
      },
      {
        key: "READY",
        label: "Sẵn sàng tại kệ",
        count: planningStats.readyCount,
        icon: CheckCircle2,
        variant: "emerald",
        tooltip: "Đã nghiệm thu vào kệ kho, sẵn sàng chạy máy",
      },
    ],
    [planningStats],
  );

  const activeCatalogFilterCount = countNonEmptyFilters(filterValues);
  const activePlanningFilterCount = countNonEmptyFilters(planningFilterValues);
  const isActiveFilterOpen =
    currentView === "planning" ? planningShowFilter : showFilter;
  const activeInlineFilterCount =
    currentView === "planning"
      ? activePlanningFilterCount
      : activeCatalogFilterCount;

  const handleToggleInlineFilter = () => {
    if (currentView === "planning") {
      if (planningShowFilter) {
        setPlanningFilterValues({});
        setPlanningShowFilter(false);
        setPlanningPage(1);
      } else {
        setPlanningShowFilter(true);
      }
      return;
    }
    if (showFilter) {
      onClearAll();
    } else {
      onToggleFilter();
    }
  };

  const isExportBusy = isExportingCatalog || isExportingPlanning;

  return (
    <>
      <ListPageShell
        trashModule={currentView === "catalog" ? "molds" : undefined}
        tabs={
          <div className="flex items-center gap-4">
            <button
              type="button"
              data-testid="mold-tab-catalog"
              onClick={() => handleViewChange("catalog")}
              className={emsTabClass(currentView === "catalog", { size: "sm" })}
            >
              <Scissors className="w-3.5 h-3.5 shrink-0" />
              <span>Danh mục khuôn ({formatNumber(catalogAllTypesTotal)})</span>
            </button>
            <button
              type="button"
              data-testid="mold-tab-planning"
              onClick={() => handleViewChange("planning")}
              className={emsTabClass(currentView === "planning", { size: "sm" })}
            >
              <Wrench className="w-3.5 h-3.5 shrink-0" />
              <span>Kế hoạch khuôn ({formatNumber(planningAllTypesTotal)})</span>
            </button>
          </div>
        }
        unitLabel={currentView === "catalog" ? "khuôn" : "yêu cầu khuôn"}
        currentPage={currentView === "catalog" ? currentPage : planningMeta.page}
        pageSize={currentView === "catalog" ? pageSize : planningPageSize}
        totalItems={currentView === "catalog" ? totalItems : planningMeta.total}
        onPageChange={
          currentView === "catalog" ? onPageChange : (p) => setPlanningPage(p)
        }
        onPageSizeChange={
          currentView === "catalog"
            ? onPageSizeChange
            : (size) => {
                setPlanningPageSize(size);
                setPlanningPage(1);
              }
        }
        isLoading={currentView === "catalog" ? isCatalogLoading : isPlanningLoading}
        onExport={isExportBusy ? undefined : handleUnifiedExport}
        actions={
          <Button
            size="sm"
            className="h-7 bg-primary text-primary-foreground text-xs font-medium cursor-pointer shadow-2xs shrink-0"
            onClick={() =>
              navigate({
                to: "/molds/create",
                search: selectedType !== "ALL" ? { type: selectedType } : undefined,
              } as any)
            }
          >
            <Plus className="mr-1 h-3.5 w-3.5 shrink-0" /> Thêm khuôn mới
          </Button>
        }
        topBanner={
          <div className="flex flex-col divide-y divide-slate-200/80 -mx-3 -my-1 bg-white">
            {/* Dòng 2: Dải 6 Tags lọc nhanh Phân loại khuôn động từ config_category('mold_types') */}
            <div
              data-testid="mold-type-tags-row"
              className="flex items-center gap-2 px-3 py-1 w-full overflow-x-auto hide-scrollbar flex-nowrap bg-slate-50/70"
            >
              <span className="text-[11px] font-semibold text-slate-500 shrink-0 select-none">
                Phân loại:
              </span>
              <CompactKpiRibbon
                items={moldTypeRibbonItems}
                activeKey={selectedType}
                onSelect={handleSelectMoldType}
                testIdPrefix="mold-type-tag"
                className="h-7 w-auto flex-1 min-w-0"
              />
            </div>

            {/* Dòng 3: Bộ lọc Ngày Pos 0 (chỉ trên Kế hoạch khuôn) + Nút Lọc + Dải Tags Trạng thái */}
            <div
              data-testid="mold-status-tags-row"
              className="erp-list-filter-row flex items-center gap-1.5 sm:gap-2 px-3 py-1 w-full overflow-x-auto hide-scrollbar flex-nowrap bg-white"
            >
              {currentView === "planning" && (
                <div className="date-filter-pos0 flex items-center gap-1.5 shrink-0">
                  <DatePeriodFilter
                    period={planningDate.period}
                    setPeriod={(val) => {
                      planningDate.setPeriod(val);
                      setPlanningPage(1);
                    }}
                    customFrom={planningDate.customFrom}
                    setCustomFrom={(val) => {
                      planningDate.setCustomFrom(val);
                      setPlanningPage(1);
                    }}
                    customTo={planningDate.customTo}
                    setCustomTo={(val) => {
                      planningDate.setCustomTo(val);
                      setPlanningPage(1);
                    }}
                  />
                  <div className="h-5 w-px bg-slate-200 shrink-0 mx-0.5" />
                </div>
              )}

              {isActiveFilterOpen ? (
                <Button
                  variant="outline"
                  size="sm"
                  data-testid="mold-inline-filter-toggle"
                  className="h-7 px-2 text-[11px] sm:text-xs gap-1 border-red-200 text-red-600 hover:bg-red-50 inline-flex shrink-0 font-medium cursor-pointer"
                  onClick={handleToggleInlineFilter}
                >
                  <X className="h-3.5 w-3.5 shrink-0" />
                  <span>Hủy lọc</span>
                  {activeInlineFilterCount > 0 && (
                    <Badge className="h-4 px-1 text-[9px] ml-0.5 bg-red-500">
                      {activeInlineFilterCount}
                    </Badge>
                  )}
                </Button>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  data-testid="mold-inline-filter-toggle"
                  className="h-7 px-2 text-[11px] sm:text-xs gap-1 inline-flex shrink-0 font-medium cursor-pointer"
                  onClick={handleToggleInlineFilter}
                >
                  <Filter className="h-3.5 w-3.5 shrink-0" />
                  <span>Lọc</span>
                </Button>
              )}

              <div className="h-5 w-px bg-slate-200 shrink-0 mx-0.5" />

              {currentView === "planning" ? (
                <CompactKpiRibbon
                  items={planningStatusRibbonItems}
                  activeKey={planningStatus}
                  onSelect={handleSelectPlanningStatus}
                  testIdPrefix="planning-status-tag"
                  className="h-7 w-auto flex-1 min-w-0 [&>div]:gap-1 [&_[role=button]]:px-2 [&_[role=button]]:gap-1"
                />
              ) : (
                <CompactKpiRibbon
                  items={catalogStatusRibbonItems}
                  activeKey={activeCatalogStatusKey}
                  onSelect={handleSelectCatalogStatus}
                  testIdPrefix="catalog-status-tag"
                  className="h-7 w-auto flex-1 min-w-0 [&>div]:gap-1 [&_[role=button]]:px-2 [&_[role=button]]:gap-1"
                />
              )}
            </div>
          </div>
        }
      >
        {currentView === "planning" ? (
          <ToolingTrackingBoard
            items={planningItems}
            isLoading={isPlanningLoading}
            onRefetch={refetchPlanning}
            showFilter={planningShowFilter}
            filterValues={planningFilterValues}
            onFilterChange={(next) => {
              setPlanningFilterValues(next);
              setPlanningPage(1);
            }}
            taxonomyByCode={taxonomyByCode}
          />
        ) : (
          <table
            aria-label="Danh mục khuôn"
            className={cn(
              "w-full text-xs caption-bottom apv-compact-table",
              selectionMode && "apv-select-mode",
            )}
          >
            <thead className="apv-table-header">
              <tr className="whitespace-nowrap">
                <th scope="col" className="apv-col-checkbox w-10 text-center whitespace-nowrap">
                  <Checkbox
                    checked={allSelected}
                    onCheckedChange={toggleSelectAll}
                  />
                </th>
                <SortableHeader
                  label="Mã Khuôn"
                  field="dieCutId"
                  currentField={sortField}
                  currentOrder={sortOrder}
                  onSort={handleSort}
                  className="apv-col-code w-[140px] whitespace-nowrap"
                />
                <SortableHeader
                  label="Tên Khuôn / SP"
                  field="name"
                  currentField={sortField}
                  currentOrder={sortOrder}
                  onSort={handleSort}
                  className="min-w-[180px] whitespace-nowrap"
                />
                <SortableHeader
                  label="Phân Loại"
                  field="type"
                  currentField={sortField}
                  currentOrder={sortOrder}
                  onSort={handleSort}
                  className="w-[130px] whitespace-nowrap"
                />
                <SortableHeader
                  label="Vị Trí Kệ"
                  field="location"
                  currentField={sortField}
                  currentOrder={sortOrder}
                  onSort={handleSort}
                  className="w-[130px] whitespace-nowrap"
                />
                <SortableHeader
                  label="Lượt Dập"
                  field="usageCount"
                  currentField={sortField}
                  currentOrder={sortOrder}
                  onSort={handleSort}
                  className="w-[120px] text-right whitespace-nowrap"
                />
                <th
                  scope="col"
                  className="text-center font-semibold text-[11px] uppercase w-[120px] whitespace-nowrap"
                >
                  Trạng Thái
                </th>
                <th
                  scope="col"
                  className="apv-col-actions w-[50px] text-center whitespace-nowrap"
                ></th>
              </tr>
              {showFilter && (
                <InlineFilterRow
                  columns={catalogFilterColumns as any}
                  filterFields={catalogFilterFields}
                  filterValues={filterValues}
                  onFilterChange={onFilterChange}
                />
              )}
            </thead>
            <tbody>
              {isCatalogLoading ? (
                Array.from({ length: 8 }).map((_, i) => (
                  <tr key={i}>
                    {Array.from({ length: 8 }).map((_, j) => (
                      <td key={j} className="px-2 py-2">
                        <Skeleton className="h-3 w-14" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : displayItems.length === 0 ? (
                <tr>
                  <td colSpan={8} className="text-center py-12 text-slate-400">
                    Không có dữ liệu khuôn phù hợp bộ lọc
                  </td>
                </tr>
              ) : (
                displayItems.map((mold: any) => {
                  const moldKey = mold.moldId || mold.dieCutId || mold.id;
                  const canonicalType = normalizeMoldTypeCode(mold.type);
                  const visualMeta =
                    taxonomyByCode.get(canonicalType) ||
                    getMoldTypeVisualMeta(mold.type);
                  const firstImage =
                    Array.isArray(mold.images) && mold.images.length > 0
                      ? mold.images[0]
                      : mold.image;
                  const customerFullName =
                    mold.customer?.fullName || mold.customer?.name || "";

                  return (
                    <DataTableRow
                      key={moldKey}
                      id={moldKey}
                      rowSelection={rowSelection}
                      className="border-b border-slate-100 last:border-0 group whitespace-nowrap"
                      onClick={() => {
                        navigate({
                          to: "/molds/$moldId",
                          params: { moldId: moldKey },
                        });
                      }}
                    >
                      <td
                        className="apv-col-checkbox"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Checkbox
                          checked={selectedIds.has(moldKey)}
                          onCheckedChange={() => toggleSelect(moldKey)}
                        />
                      </td>
                      <td className="apv-col-code px-2 py-1.5">
                        <div className="flex items-center gap-1.5">
                          <span
                            className={cn(
                              "px-1.5 py-0.2 text-[10px] font-bold rounded border uppercase shrink-0",
                              visualMeta.badgeClass,
                            )}
                            title={visualMeta.fullLabel}
                          >
                            {visualMeta.prefix}
                          </span>
                          <span className="font-mono font-bold text-indigo-600">
                            {moldKey}
                          </span>
                          <Badge
                            variant="outline"
                            className="text-[9px] px-1 py-0 bg-slate-50 text-slate-600 font-mono shrink-0"
                          >
                            v{mold.version || 1}
                          </Badge>
                        </div>
                        {mold.replacedBy && (
                          <div className="text-[9px] text-amber-600 font-medium mt-0.5 flex items-center gap-0.5">
                            <ArrowUpRight className="w-2.5 h-2.5" /> Thay bởi{" "}
                            {mold.replacedBy.dieCutId || `v${(mold.version || 1) + 1}`}
                          </div>
                        )}
                        {mold.replaces && (
                          <div className="text-[9px] text-emerald-600 font-medium mt-0.5">
                            Thay cho {mold.replaces.dieCutId}
                          </div>
                        )}
                      </td>
                      <td className="px-2 py-1.5">
                        <div className="flex items-center gap-2">
                          {firstImage ? (
                            <img
                              src={getFullUrl(firstImage)}
                              alt={mold.name}
                              className="w-8 h-8 rounded border border-slate-200 object-cover shrink-0"
                              onError={(e) => {
                                (e.target as HTMLElement).style.display = "none";
                              }}
                            />
                          ) : (
                            <div className="w-8 h-8 rounded bg-slate-100 border border-slate-200 flex items-center justify-center shrink-0 text-slate-400">
                              <ImageIcon className="w-4 h-4" />
                            </div>
                          )}
                          <div className="min-w-0">
                            <div className="font-semibold text-slate-900 group-hover:text-indigo-600 transition-colors uppercase truncate max-w-[240px]">
                              {mold.name || "—"}
                            </div>
                            <div className="text-[10px] text-slate-500 truncate max-w-[260px] flex items-center gap-1">
                              <span className="truncate" title={customerFullName || undefined}>
                                {mold.productName ||
                                  mold.product?.productName ||
                                  mold.product?.name ||
                                  (customerFullName ? `KH: ${customerFullName}` : "—")}
                              </span>
                              {mold.dieCutProducts && mold.dieCutProducts.length > 1 && (
                                <Badge
                                  variant="outline"
                                  className="text-[9px] px-1 py-0 bg-indigo-50 text-indigo-700 border-indigo-200 shrink-0 font-bold"
                                  title={mold.dieCutProducts
                                    .map((p: any) => p.product?.productName || p.product?.productCode)
                                    .filter(Boolean)
                                    .join(", ")}
                                >
                                  +{mold.dieCutProducts.length - 1} SP
                                </Badge>
                              )}
                            </div>
                            {mold.printLayouts && mold.printLayouts.length > 0 ? (
                              <div className="text-[9px] text-purple-600 font-mono mt-0.5 truncate">
                                Bình:{" "}
                                {mold.printLayouts[0].layoutCode || mold.printLayouts[0].name}
                              </div>
                            ) : mold.layout ? (
                              <div className="text-[9px] text-purple-600 font-mono mt-0.5 truncate">
                                Bình: {mold.layout.layoutId}
                              </div>
                            ) : null}
                          </div>
                        </div>
                      </td>
                      <td className="px-2 py-1.5">
                        <span
                          className={cn(
                            "inline-flex items-center px-2 py-0.5 text-[11px] font-medium rounded-full border",
                            visualMeta.badgeClass,
                          )}
                          title={visualMeta.fullLabel}
                        >
                          {visualMeta.shortLabel}
                        </span>
                      </td>
                      <td className="px-2 py-1.5">
                        <div className="flex items-center text-slate-700 font-medium">
                          <MapPin className="h-3 w-3 mr-1 text-slate-400 shrink-0" />
                          <span className="truncate font-semibold text-slate-900">
                            {formatLocation(mold.location) ||
                              (mold.storageRack
                                ? `${mold.storageZone || "Kho"} • ${mold.storageRack} • ${mold.storageShelf || ""}`
                                : "Chưa xếp kệ")}
                          </span>
                        </div>
                        <div className="text-[9px] font-medium text-slate-400 uppercase tracking-tight mt-0.5">
                          {mold.storageBin ? `Ô: ${mold.storageBin} • ` : ""}
                          {mold.machine || "Chưa gán máy"}
                        </div>
                      </td>
                      <td className="px-2 py-1.5 text-right">
                        <MoldWearBar
                          usageCount={mold.usageCount}
                          maxImpressions={mold.maxImpressions}
                          maintenanceInterval={mold.maintenanceInterval}
                          lastMaintenanceImpressions={mold.lastMaintenanceImpressions}
                          compact={true}
                        />
                      </td>
                      <td className="px-2 py-1.5 text-center">
                        <div className="flex justify-center">
                          <StatusBadge type="ToolingStatus" value={mold.status} />
                        </div>
                      </td>
                      <td
                        className="apv-col-actions px-2 py-1.5 text-center"
                        onClick={(e: React.MouseEvent) => e.stopPropagation()}
                      >
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 hover:bg-slate-100 text-slate-500 hover:text-indigo-600"
                              title="Thao tác"
                            >
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent
                            align="end"
                            className="w-52 z-[9999]"
                          >
                            <DropdownMenuLabel className="text-xs text-slate-500">
                              Thao tác khuôn
                            </DropdownMenuLabel>
                            <DropdownMenuItem
                              onClick={() =>
                                navigate({
                                  to: "/molds/$moldId/edit",
                                  params: { moldId: moldKey },
                                } as any)
                              }
                            >
                              <Pencil className="mr-2 h-4 w-4 text-slate-400" /> Chỉnh sửa
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => setRelocateMold(mold)}
                            >
                              <MapPin className="mr-2 h-4 w-4 text-blue-500" /> Đổi vị trí kệ kho
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => setPrintLabelMold(mold)}
                            >
                              <Tag className="mr-2 h-4 w-4 text-indigo-500" /> In tem gáy QR
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            {mold.status !== "AVAILABLE" && mold.status !== "DISCARDED" && (
                              <DropdownMenuItem
                                onClick={() =>
                                  updateStatusMutation.mutate({
                                    id: mold.id || moldKey,
                                    status: "AVAILABLE",
                                  })
                                }
                              >
                                <Scissors className="mr-2 h-4 w-4 text-emerald-500" /> Sẵn sàng sử dụng
                              </DropdownMenuItem>
                            )}
                            {mold.status !== "MAINTENANCE" && mold.status !== "DISCARDED" && (
                              <DropdownMenuItem
                                onClick={() =>
                                  updateStatusMutation.mutate({
                                    id: mold.id || moldKey,
                                    status: "MAINTENANCE",
                                  })
                                }
                              >
                                <Wrench className="mr-2 h-4 w-4 text-amber-500" /> Chuyển bảo dưỡng
                              </DropdownMenuItem>
                            )}
                            {mold.status !== "DAMAGED" && mold.status !== "DISCARDED" && (
                              <DropdownMenuItem
                                onClick={() =>
                                  updateStatusMutation.mutate({
                                    id: mold.id || moldKey,
                                    status: "DAMAGED",
                                  })
                                }
                              >
                                <AlertCircle className="mr-2 h-4 w-4 text-rose-500" /> Báo hỏng khuôn
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              className="text-rose-600 focus:text-rose-700"
                              onClick={() =>
                                setMoldToDelete({
                                  id: mold.id || moldKey,
                                  name: mold.name || mold.dieCutId || moldKey,
                                })
                              }
                            >
                              <Trash2 className="mr-2 h-4 w-4" /> Xóa khuôn
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </DataTableRow>
                  );
                })
              )}
            </tbody>
          </table>
        )}
      </ListPageShell>

      {currentView === "catalog" && (
        <BulkActionBar
          selectedCount={selectedCount}
          onClearSelection={clearSelection}
          actions={getCatalogBulkActions()}
        />
      )}

      <AlertDialog
        open={!!moldToDelete}
        onOpenChange={(open) => !open && setMoldToDelete(null)}
      >
        <AlertDialogContent className="z-[10000]">
          <AlertDialogHeader>
            <AlertDialogTitle>Xác nhận xóa khuôn?</AlertDialogTitle>
            <AlertDialogDescription>
              Bạn có chắc chắn muốn xóa khuôn{" "}
              <strong>{moldToDelete?.name}</strong>? Hành động này không thể hoàn tác nếu khuôn chưa từng gắn vào Lệnh sản xuất.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Hủy</AlertDialogCancel>
            <AlertDialogAction
              className="bg-rose-600 hover:bg-rose-700 text-white"
              onClick={() => moldToDelete && deleteMutation.mutate(moldToDelete.id)}
            >
              {deleteMutation.isPending ? "Đang xóa..." : "Xác nhận xóa"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Modal: Đổi vị trí kệ kho */}
      <QuickRelocateModal
        open={!!relocateMold}
        onOpenChange={(open) => !open && setRelocateMold(null)}
        tooling={relocateMold}
        toolType="mold"
      />

      {/* Modal: In tem gáy dán khuôn QR */}
      {printLabelMold && (
        <PrintDialog
          open={!!printLabelMold}
          onClose={() => setPrintLabelMold(null)}
          type="custom"
          documentTitle={`Tem_Gay_Khuon_${printLabelMold.dieCutId || printLabelMold.moldId || printLabelMold.id}`}
        >
          <MoldLabelPrintTemplate data={printLabelMold} />
        </PrintDialog>
      )}
    </>
  );
}
